import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { color, r, radius } from '../../theme/tokens';
import RiskChip from '../stack/RiskChip';
import InteractionDetailsModal from '../common/InteractionDetailsModal';
import { checkedAs } from './copy';

// v5.1's results (STACK-587, states 8–10 and 12): one row per supplement,
// flagged first and worst first. A flagged row carries `RiskChip` at its
// highest risk, which opens the shipped `InteractionDetailsModal` unchanged,
// and names the medications it was flagged with; a clean row reads "Not
// flagged with …". Rows come only from a completed check (resultRows.js).
// On desktop the chip sits at the row's end (state 12).

const CapsuleIcon = () => (
  <Box component="svg" viewBox="0 0 14 14" aria-hidden="true" focusable="false" sx={{ width: r(20), height: r(20) }}>
    <rect x="1.5" y="4.5" width="11" height="5" rx="2.5" transform="rotate(-45 7 7)" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path d="M5 5l4 4" stroke="currentColor" strokeWidth="1.2" />
  </Box>
);

const ResultRow = ({ row, onOpen }) => (
  <Box
    component="li"
    data-testid="checker-result-row"
    data-flagged={row.level ? 'true' : 'false'}
    sx={{
      display: 'flex',
      alignItems: { xs: 'flex-start', md: 'center' },
      gap: r(13),
      px: r(18),
      py: r(16),
      backgroundColor: color.paper,
      '& + &': { borderTop: `${r(1)} solid ${color.lineSoft}` },
    }}
  >
    <Box
      sx={{
        flex: 'none',
        width: r(44),
        height: r(44),
        borderRadius: '50%',
        backgroundColor: color.mint,
        color: color.evergreen,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <CapsuleIcon />
    </Box>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'grid',
        // Phone: name, chip, line, as v5.1 draws it. Desktop: the chip at the
        // row's end (state 12).
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' },
        gridTemplateAreas: {
          xs: '"name" "chip" "sub"',
          md: '"name chip" "sub chip"',
        },
        alignItems: { xs: 'start', md: 'center' },
        columnGap: r(16),
        rowGap: r(6),
      }}
    >
      <Typography component="h3" sx={{ gridArea: 'name', m: 0, fontSize: r(16), fontWeight: 700, lineHeight: 1.3, color: color.ink }}>
        {row.name}
      </Typography>
      {row.level && (
        <Box sx={{ gridArea: 'chip', justifySelf: 'start' }}>
          <RiskChip level={row.level} count={row.interaction.interactionCount} onClick={() => onOpen(row)} />
        </Box>
      )}
      <Typography sx={{ gridArea: 'sub', m: 0, fontSize: r(13), lineHeight: 1.45, color: color.inkSoft }}>{row.sub}</Typography>
    </Box>
  </Box>
);

/**
 * @param {Object} props
 * @param {Array} props.rows - from buildResultRows
 * @param {Array} props.medications - the medications checked
 */
export default function CheckerResults({ rows, medications }) {
  const [open, setOpen] = useState(null);

  // Each medication names the one ingredient it was checked as, where its
  // name does not already say it (a brand, a combination product: "Hyzaar:
  // checked as losartan"; until STACK-584).
  const checkedAsLines = medications
    .filter((m) => m.ingredient_name && !m.display_name.toLowerCase().includes(m.ingredient_name.toLowerCase()))
    .map((m) => checkedAs(m.display_name, m.ingredient_name));

  return (
    <>
      <Box
        component="ul"
        aria-label="Results"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          border: `${r(1)} solid ${color.line}`,
          borderRadius: radius.lg,
          overflow: 'hidden',
        }}
      >
        {rows.map((row) => <ResultRow key={row.key} row={row} onOpen={setOpen} />)}
      </Box>
      {checkedAsLines.length > 0 && (
        <Box component="ul" data-testid="checker-checked-as" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {checkedAsLines.map((line) => (
            <Typography key={line} component="li" sx={{ fontSize: r(13), lineHeight: 1.45, color: color.inkSoft }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}
      <InteractionDetailsModal
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        supplementName={open?.name}
        interactions={open?.interaction.allInteractions || []}
        primaryInteraction={open?.interaction.primaryInteraction}
      />
    </>
  );
}
